import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowRight, Mail, Lock, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CRM } from "@/constants/testIds";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  useSeo({ title: "Acceso privado", description: "Acceso privado para inversionistas y administración de Expansión Rubí.", path: "/login", noindex: true });
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const portalHint = params.get("portal") === "1";
  const [tab, setTab] = useState(portalHint ? "portal" : "staff");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.must_change_password) {
      navigate("/portal/password", { replace: true });
    } else if (user.role === "interesado" || user.role === "inversionista") {
      navigate("/portal", { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-login", form);
      if (setUser) setUser(data.user);
      toast.success(`Bienvenido, ${data.user.name.split(" ")[0]}`);
      const isPortal = data.user.role === "interesado" || data.user.role === "inversionista";
      const dest = data.user.must_change_password ? "/portal/password" : isPortal ? "/portal" : "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-white/10">
        <img
          src="https://images.pexels.com/photos/8730119/pexels-photo-8730119.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1400"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="eyebrow">Expansión Rubí</p>
          <h2 className="font-display text-5xl mt-4 max-w-md">Tu espacio privado, siempre a un click.</h2>
          <p className="text-neutral-400 mt-4 max-w-md text-sm leading-relaxed">
            Acceso para el equipo comercial (Google) o para prospectos e inversionistas (correo + contraseña).
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="text-neutral-500 hover:text-[#FFBF57] text-xs uppercase tracking-widest">← Volver</Link>

          {/* Tabs */}
          <div className="mt-8 grid grid-cols-2 gap-1 border border-white/10 rounded-full p-1" role="tablist">
            <button
              onClick={() => setTab("staff")}
              className={`text-xs uppercase tracking-widest font-mono py-2 rounded-full transition-all ${tab === "staff" ? "bg-[#FFBF57] text-black" : "text-neutral-400 hover:text-white"}`}
              data-testid="login-tab-staff"
            >
              Equipo · Google
            </button>
            <button
              onClick={() => setTab("portal")}
              className={`text-xs uppercase tracking-widest font-mono py-2 rounded-full transition-all ${tab === "portal" ? "bg-[#FFBF57] text-black" : "text-neutral-400 hover:text-white"}`}
              data-testid="login-tab-portal"
            >
              Portal · Correo
            </button>
          </div>

          {tab === "staff" ? (
            <>
              <p className="eyebrow mt-8">CRM interno</p>
              <h1 className="font-display text-4xl mt-3">Iniciar sesión</h1>
              <p className="text-neutral-400 mt-3 text-sm">Autenticación segura con Google. Sólo cuentas autorizadas por dirección.</p>
              <button data-testid={CRM.loginBtn} onClick={handleGoogle} className="btn-primary w-full mt-8 justify-center">
                Continuar con Google <ArrowRight size={16} strokeWidth={1.5} />
              </button>
              <div className="mt-6 flex items-start gap-3 text-xs text-neutral-500 leading-relaxed">
                <ShieldCheck size={16} strokeWidth={1.5} className="mt-0.5 text-[#FFBF57]" />
                <span>Al iniciar sesión aceptas que tu actividad quede registrada para auditoría interna.</span>
              </div>
            </>
          ) : (
            <>
              <p className="eyebrow mt-8">Portal privado</p>
              <h1 className="font-display text-4xl mt-3">Acceder al portal</h1>
              <p className="text-neutral-400 mt-3 text-sm">Cuenta creada por tu asesor comercial. Usa el correo que le proporcionaste + la contraseña inicial <span className="text-[#FFBF57] font-mono">expansion2026</span>.</p>
              <form onSubmit={handlePasswordLogin} className="mt-8 space-y-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Correo</span>
                  <div className="mt-1 relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full bg-transparent border border-white/15 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white focus:border-[#FFBF57] outline-none"
                      placeholder="tucorreo@ejemplo.com"
                      data-testid="portal-login-email"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Contraseña</span>
                  <div className="mt-1 relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="password"
                      required
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full bg-transparent border border-white/15 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white focus:border-[#FFBF57] outline-none"
                      placeholder="expansion2026"
                      data-testid="portal-login-password"
                    />
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary w-full justify-center mt-2"
                  data-testid="portal-login-submit"
                >
                  {busy ? "Verificando…" : (<>Entrar al portal <ChevronRight size={16} /></>)}
                </button>
              </form>
              <div className="mt-6 text-[11px] text-neutral-500 leading-relaxed border-t border-white/5 pt-4">
                ¿No tienes cuenta? Solicítala con tu asesor comercial de Rubí.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
